import React, { useMemo } from "react";
import { Typography } from "@mui/material";
import ConnectionAccordionSection from "../../../../components/view/sections/ConnectionAccordionSection";
import EntityReferenceComponent from "../../../../components/entitiesReferences/EntityReferenceComponent";
import { ViewAccordionField } from "../../../../components/view/fields/ViewAccordion";
import { RichTextEditor } from "../../../../components/field/richTextEditor/RichTextEditor";
import useReferenceVersionQuery from "../../../../hooks/query/connections/useReferenceVersionQuery";
import {
  ReferenceVersionConnection,
  SystemVersionConnectionDirection,
  SystemVersionReferenceToEntity,
} from "../../../../types/referenceVersion";
import { Version } from "../../../../types/version";

interface ReferenceVersionsViewSectionProps {
  anchor: Version;
}

type ReferenceDirection = "outbound" | "inbound";

type ReferenceVersionViewItem = ReferenceVersionConnection & {
  viewDirection: ReferenceDirection;
};

const displayText = (value: unknown): string =>
  value !== undefined && value !== null && value !== "" ? String(value) : "N/A";

const CONNECTION_TYPE_LABELS: Record<SystemVersionConnectionDirection, string> = {
  [SystemVersionConnectionDirection.REFERRING_TO]: "Referring To",
  [SystemVersionConnectionDirection.REFERENCED_BY]: "Referenced By",
};

const REFERENCE_TO_ENTITY_LABELS: Record<SystemVersionReferenceToEntity, string> =
  {
    [SystemVersionReferenceToEntity.PERSON]: "Person",
    [SystemVersionReferenceToEntity.VERSION]: "Version",
  };

const formatReferenceToEntity = (
  value: SystemVersionReferenceToEntity | null
): string => (value ? REFERENCE_TO_ENTITY_LABELS[value] : "N/A");

const resolveConnectionDirection = (
  item: ReferenceVersionViewItem
): SystemVersionConnectionDirection => {
  const stored =
    item?.system_referring_to_version_connection_type ??
    SystemVersionConnectionDirection.REFERRING_TO;
  if (item.viewDirection === "outbound") return stored;
  return stored === SystemVersionConnectionDirection.REFERRING_TO
    ? SystemVersionConnectionDirection.REFERENCED_BY
    : SystemVersionConnectionDirection.REFERRING_TO;
};

const resolveConnectionLabel = (item: ReferenceVersionViewItem): string =>
  CONNECTION_TYPE_LABELS[resolveConnectionDirection(item)];

const getReferenceFields = (
  item: ReferenceVersionViewItem
): ViewAccordionField[] => [
  ...(item.viewDirection === "outbound"
    ? [
        {
          label: "Reference To Entity",
          value: (
            <Typography variant="body1">
              {formatReferenceToEntity(item?.system_reference_to_entity ?? null)}
            </Typography>
          ),
        },
      ]
    : []),
  {
    label: "Reference Type",
    value: (
      <Typography variant="body1">
        {displayText(item?.version_reference_type)}
      </Typography>
    ),
  },
  {
    label: "Connection Type",
    value: (
      <Typography variant="body1">{resolveConnectionLabel(item)}</Typography>
    ),
  },
  {
    label: "Notes",
    value: (
      <RichTextEditor
        value={typeof item?.notes === "string" ? item.notes : ""}
        readOnly
        fullWidth
        placeholder="N/A"
      />
    ),
  },
];

const ReferenceVersionsViewSection: React.FC<
  ReferenceVersionsViewSectionProps
> = ({ anchor }) => {
  const { useFetchConnectedEdges } = useReferenceVersionQuery();
  const outbound = useFetchConnectedEdges(anchor?._id, "outbound", {});
  const inbound = useFetchConnectedEdges(anchor?._id, "inbound", {});

  const items = useMemo<ReferenceVersionViewItem[]>(
    () => [
      ...((outbound.data?.data ?? []).map((edge) => ({
        ...edge,
        viewDirection: "outbound" as const,
      })) as ReferenceVersionViewItem[]),
      ...((inbound.data?.data ?? []).map((edge) => ({
        ...edge,
        viewDirection: "inbound" as const,
      })) as ReferenceVersionViewItem[]),
    ],
    [outbound.data, inbound.data]
  );

  const loading = outbound.isLoading || inbound.isLoading;
  const error = outbound.errorMessage || inbound.errorMessage;

  return (
    <ConnectionAccordionSection<ReferenceVersionViewItem>
      title="Reference Versions"
      loading={loading}
      error={error}
      items={items}
      getItemId={(item, index) =>
        `${item.viewDirection}-${item?._key ?? `reference-version-${index}`}`
      }
      getItemTitle={(item) => {
        const entity =
          item.viewDirection === "outbound" ? item?.toDoc : item?.fromDoc;
        return entity ? (
          <EntityReferenceComponent entity={entity} linkMode="view" />
        ) : (
          <Typography variant="body1">N/A</Typography>
        );
      }}
      getItemSubtitle={(item) => resolveConnectionLabel(item)}
      getItemFields={(item) => getReferenceFields(item)}
      emptyText="No reference versions"
    />
  );
};

export default ReferenceVersionsViewSection;
